import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Eye, EyeOff, Trash2, Lock, Unlock } from 'lucide-react';
import { formatFieldLabel, formatSeriesLabel } from '../../utils/formatFieldLabel';
import { MeasureConfig, Aggregation } from '../../types';

interface YSeriesCardProps {
    measure: MeasureConfig;
    index: number;
    onChange: (index: number, updates: Partial<MeasureConfig>) => void;
    onDelete: (index: number) => void;
    numericCols: { label: string; value: string }[];
    showColorPicker?: boolean;
    resolvedColor?: string;
    palette?: string[];
}

export const YSeriesCard: React.FC<YSeriesCardProps> = ({
    measure,
    index,
    onChange,
    onDelete,
    numericCols,
    showColorPicker = true,
    resolvedColor,
    palette = []
}) => {
    const [expanded, setExpanded] = useState(!measure.field);

    // Fallback color logic: Use resolvedColor -> custom color -> palette color -> default gold
    const displayColor = measure.colorMode === 'custom' && measure.color
        ? measure.color
        : (resolvedColor || (palette.length > 0 ? palette[index % palette.length] : '#f59e0b'));


    // Handle field change
    const handleFieldChange = (field: string) => {
        const updates: Partial<MeasureConfig> = { field };

        // Auto-update display name if not custom
        if (!measure.isCustomLabel) {
            updates.displayName = formatSeriesLabel(field, measure.aggregation);
        }

        onChange(index, updates);
    };

    // Handle aggregation change
    const handleAggregationChange = (aggregation: Aggregation) => {
        console.log(`[YSeriesCard] Aggregation changed for measure "${measure.field}" (${measure.id}): ${measure.aggregation} -> ${aggregation}`);

        const updates: Partial<MeasureConfig> = { aggregation };

        // Auto-update display name if not custom
        if (!measure.isCustomLabel) {
            updates.displayName = formatSeriesLabel(measure.field, aggregation);
        }

        onChange(index, updates);
    };

    // Handle display name change
    const handleNameChange = (displayName: string) => {
        onChange(index, {
            displayName,
            isCustomLabel: true // Lock it
        });
    };

    // Unlock display name (reset to auto)
    const handleUnlockName = () => {
        onChange(index, {
            isCustomLabel: false,
            displayName: formatSeriesLabel(measure.field, measure.aggregation)
        });
    };

    // Handle color change
    const handleColorChange = (color: string) => {
        onChange(index, {
            colorMode: 'custom',
            color
        });
    };

    // Reset color to auto
    const handleResetColor = () => {
        onChange(index, {
            colorMode: 'auto',
            color: undefined
        });
    };

    return (
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden transition-all hover:border-white/20">
            {/* Collapsed View */}
            <div className="flex items-center gap-2 p-2 bg-black/20">
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="p-1 hover:bg-white/10 rounded"
                >
                    {expanded ? (
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                    ) : (
                        <ChevronRight className="w-3 h-3 text-gray-400" />
                    )}
                </button>

                <div className="flex-1 min-w-0 flex items-center gap-2">
                    {/* Color Preview Dot */}
                    <div
                        className="w-2 h-6 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: displayColor }}
                    />

                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-200 truncate flex items-center gap-1">
                            {measure.displayName}
                            {measure.isCustomLabel && <Lock className="w-2.5 h-2.5 text-gold-500/50" />}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                            {formatFieldLabel(measure.field)}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <button
                    onClick={() => onChange(index, { visible: !measure.visible })}
                    className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                    title={measure.visible ? "Hide Series" : "Show Series"}
                >
                    {measure.visible ? (
                        <Eye className="w-3.5 h-3.5" />
                    ) : (
                        <EyeOff className="w-3.5 h-3.5" />
                    )}
                </button>

                <button
                    onClick={() => onDelete(index)}
                    className="p-1.5 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400 transition-colors"
                    title="Remove Series"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Expanded View */}
            {expanded && (
                <div className="p-2 space-y-2 border-t border-white/5 bg-black/10">
                    {/* Field Selector */}
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Field</label>
                        <select
                            value={measure.field}
                            onChange={(e) => handleFieldChange(e.target.value)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                        >
                            <option value="">Select Field</option>
                            {numericCols.map(col => (
                                <option key={col.value} value={col.value}>
                                    {col.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Aggregation */}
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-400">Aggregation</label>
                        <select
                            value={measure.aggregation}
                            onChange={(e) => handleAggregationChange(e.target.value as Aggregation)}
                            className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                        >
                            <option value="sum">Sum</option>
                            <option value="avg">Average</option>
                            <option value="min">Minimum</option>
                            <option value="max">Maximum</option>
                            <option value="count">Count</option>
                        </select>
                    </div>

                    {/* Display Name */}
                    <div className="space-y-1">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-semibold text-gray-400">Display Name</label>
                            {measure.isCustomLabel && (
                                <button
                                    onClick={handleUnlockName}
                                    className="text-[10px] text-gold-500 hover:text-gold-400 flex items-center gap-1"
                                    title="Reset to auto-generated name"
                                >
                                    <Unlock className="w-3 h-3" /> Reset Auto
                                </button>
                            )}
                        </div>
                        <input
                            type="text"
                            value={measure.displayName}
                            onChange={(e) => handleNameChange(e.target.value)}
                            className={`w-full text-sm px-2 py-1.5 bg-obsidian-800 border rounded-lg text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-gold-500/50 ${measure.isCustomLabel ? 'border-gold-500/50' : 'border-gold-500/20'}`}
                        />
                    </div>

                    {/* Color */}
                    {showColorPicker && (
                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-gray-400">Color</label>
                                {measure.colorMode === 'custom' && (
                                    <button
                                        onClick={handleResetColor}
                                        className="text-[10px] text-gray-500 hover:text-white"
                                    >
                                        Reset to Auto
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={displayColor}
                                    onChange={(e) => handleColorChange(e.target.value)}
                                    className="h-6 w-8 rounded bg-transparent border border-white/20 cursor-pointer"
                                />
                                <span className="text-xs text-gray-500">
                                    {measure.colorMode === 'auto' ? 'Auto (Palette)' : 'Custom'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
